import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, isFirebaseConfigured } from './firebase';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  isFirebaseConfigured: boolean;
  /// Email + password sign-in. Same Firebase Auth used by the iOS
  /// Parrot app, so credentials created on iOS work here unchanged.
  signInWithEmail: (email: string, password: string) => Promise<void>;
  /// Google popup. Kept as a secondary option for accounts created via
  /// Apple/Google on iOS that don't have an email/password set yet.
  signInWithGoogle: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  signOutUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Bootstrap admin allowlist. Firestore `admin/{email}` docs are the
 * manageable source for day-to-day access, but this keeps the owner
 * account from getting locked out if the admin table is empty.
 *
 * Mirror these emails in firestore.rules so the server enforces the
 * same bootstrap gate. The client check is purely UX.
 */
export const BOOTSTRAP_ADMIN_EMAILS: ReadonlyArray<string> = [
  'tremaine.grant@gmail.com',
];

const bootstrapAdminEmails: string[] = BOOTSTRAP_ADMIN_EMAILS.map((s) => s.toLowerCase());

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(isFirebaseConfigured);
  const [firestoreAdmin, setFirestoreAdmin] = useState<boolean>(false);

  useEffect(() => {
    if (!auth) {
      // Firebase isn't configured (missing env vars). Marketing routes
      // still render; admin routes will surface a setup banner via
      // AdminGate.
      setLoading(false);
      return;
    }
    let checkId = 0;
    const unsub = onAuthStateChanged(auth, async (u) => {
      const currentCheckId = ++checkId;
      setUser(u);
      setFirestoreAdmin(false);

      if (!u) {
        setLoading(false);
        return;
      }

      const email = u.email?.toLowerCase() ?? '';
      if (!email || bootstrapAdminEmails.includes(email) || !db) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'admin', email));
        if (currentCheckId === checkId) {
          setFirestoreAdmin(snap.exists());
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[Parrot-web] Admin lookup failed', err);
        if (currentCheckId === checkId) {
          setFirestoreAdmin(false);
        }
      } finally {
        if (currentCheckId === checkId) {
          setLoading(false);
        }
      }
    });
    return () => {
      checkId += 1;
      unsub();
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const email = user?.email?.toLowerCase() ?? '';
    const isAdmin = !!email && (bootstrapAdminEmails.includes(email) || firestoreAdmin);
    return {
      user,
      loading,
      isAdmin,
      isFirebaseConfigured,
      signInWithEmail: async (email, password) => {
        if (!auth) {
          throw new Error('Firebase is not configured. See src/lib/firebase.ts.');
        }
        await signInWithEmailAndPassword(auth, email.trim(), password);
      },
      signInWithGoogle: async () => {
        if (!auth) {
          throw new Error('Firebase is not configured. See src/lib/firebase.ts.');
        }
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        await signInWithPopup(auth, provider);
      },
      sendPasswordReset: async (email) => {
        if (!auth) {
          throw new Error('Firebase is not configured. See src/lib/firebase.ts.');
        }
        await sendPasswordResetEmail(auth, email.trim());
      },
      signOutUser: async () => {
        if (!auth) return;
        await signOut(auth);
      },
    };
  }, [user, loading, firestoreAdmin]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
